import {createContext, use, useRef, useState} from "react";
import {api} from "@/shared/api";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import type {FieldError, SaveWorkingTimeNoticeRequest, WorkingTimeType} from "@/shared/timr";
import type {WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {WorkingTimeNoticeMode, WorkingTimeRequestStatus} from "@/shared/timrTypes";
import type {UserOption} from "@/features/requests/workingTimeNoticeDialog/useNoticeUserOptions";
import {Temporal} from "@js-temporal/polyfill";
import {getISODate} from "@/shared/utils/datefunctions";

/**
 * The selected working time type as held by the form — a thin display projection ({@code id} for the
 * payload, {@code name}/{@code category} for rendering, {@code noticeMode} for choosing between notice and
 * notice request — see {@link isNoticeRequestForm}), <em>not</em> the full {@link WorkingTimeType}.
 *
 * The form holds this object rather than a bare id for the same reason it holds a {@link UserOption}:
 * a notice may reference a type that is <em>not</em> in the current user's selectable list (e.g. a type
 * with {@code noticeMode} {@code NOT_ALLOWED} that only a leader can author). A bare id could not be
 * rendered — the {@code <select>} has no matching option — so the dialog would fall back to showing the
 * first selectable type. Carrying the name/category (seeded from the notice itself) lets the read-only
 * dialog display the real type even though it is off-list.
 */
export type WorkingTimeTypeOption = {
	id: number;
	name: string;
	category: WorkingTimeTypeCategory;
	noticeMode: WorkingTimeNoticeMode;
};

/**
 * Internal form view-model — deliberately <em>not</em> the request DTO, so it can hold richer types
 * than the wire format:
 * <ul>
 *   <li>Dates are the canonical {@link Temporal.PlainDate} type (not DTO date strings), so the controller
 *       never re-parses/-formats while editing — it parses once when seeding the form and formats once
 *       when building the save payload. {@code null} is an empty/cleared date.</li>
 *   <li>The target user is the full {@link UserOption} ({@code {id, fullname}}), not a bare id, because
 *       the combo box needs the name to display a selection that may not be in its fetched list (e.g.
 *       editing a notice for an inactive user). The DTO's {@code userId} is derived from it at save.</li>
 *   <li>The working time type is a {@link WorkingTimeTypeOption} ({@code {id, name, category, noticeMode}}), not a
 *       bare id, for the same off-list reason — see that type's doc. The DTO's {@code workingTimeTypeId}
 *       is derived from it at save.</li>
 * </ul>
 */
export interface WorkingTimeNoticeForm {
	id?: number;
	version?: number;
	selectedUser: UserOption | null;
	selectedWorkingTimeType: WorkingTimeTypeOption | null;
	description?: string;
	start: Temporal.PlainDate | null;
	end: Temporal.PlainDate | null;
}

export function isNoticeRequestForm(form: WorkingTimeNoticeForm): boolean {
	return form.selectedWorkingTimeType?.noticeMode === WorkingTimeNoticeMode.BY_REQUEST;
}

interface WorkingTimeNoticeDialogState {
	open: boolean;
	form: WorkingTimeNoticeForm;
	saving: boolean;
	fieldErrors: Record<string, string>;
}

interface WorkingTimeNoticeDialogActions {
	setWorkingTimeTypeId: (id: number) => void;
	setDescription: (value: string) => void;
	setStartDate: (value: Temporal.PlainDate | null) => void;
	setEndDate: (value: Temporal.PlainDate | null) => void;
	setSelectedUser: (user: UserOption | null) => void;
	save: () => void;
	saveAndApprove: () => void;
	deleteNotice: () => void;
	close: () => void;
}

interface WorkingTimeNoticeDialogMeta {
	showUserRow: boolean;
	/**
	 * Whether the user may be picked. True only for the create variant — the user of an existing notice must
	 * not be changed (reassignment is unsupported; see backend {@code validateUserNotChanged}, TIMR-12313), so
	 * the edit variant shows the user read-only.
	 */
	userSelectable: boolean;
	workingTimeTypes: WorkingTimeType[];
	/**
	 * True when the dialog must not be editable — the notice references a working time type the current
	 * user cannot author notices for (it is absent from {@code workingTimeTypes}). The view then renders
	 * every field as a static, disabled display and omits the save/delete actions, while still showing
	 * the notice's real type. Always false for the create variant, whose type is picked from the list.
	 */
	readOnly: boolean;
}

/**
 * Headless controller for an entity dialog (a ViewModel expressed as a hook): a provider produces
 * one instance and dependency-injects it through context, while the dumb view components consume it.
 * The value is split into three buckets by <em>what kind of thing</em> each member is — the split is
 * the contract that keeps the view decoupled from how state is actually managed.
 *
 * @typeParam S - <strong>state</strong>: the <em>reactive data</em> the view renders. Anything whose
 *   change must trigger a re-render goes here — form values, flags like {@code saving}, validation
 *   errors, and reactive inputs the view reads (e.g. whether the dialog is {@code open}). It does not
 *   matter how a provider sources it (local {@code useState}, a store, or a prop); if it is reactive
 *   data the view consumes, it is state. Derived values should be computed in render, not stored here.
 * @typeParam A - <strong>actions</strong>: the <em>behaviour</em> — event handlers / commands the view
 *   invokes ({@code save}, {@code close}, setters). Stable callbacks, not data. No return values the
 *   view renders (those belong in state).
 * @typeParam M - <strong>meta</strong>: the <em>non-reactive</em> ancillary the view needs — stable
 *   refs/handles, static options, and derived configuration that does not change during the dialog's
 *   life ({@code workingTimeTypes}, {@code showUserRow}). If a member changes
 *   over time and must re-render the view, it is state, not meta.
 */
export type EntityDialogController<S, A, M> = {
	state: S;
	actions: A;
	meta: M;
};

export type WorkingTimeNoticeDialogController = EntityDialogController<
	WorkingTimeNoticeDialogState,
	WorkingTimeNoticeDialogActions,
	WorkingTimeNoticeDialogMeta
>;

const WorkingTimeNoticeDialogContext = createContext<WorkingTimeNoticeDialogController | null>(null);

/**
 * Bridges the producer and consumer hooks: a variant root feeds the controller produced by
 * {@link useWorkingTimeNoticeDialogController} into this provider, and every descendant reads that
 * same instance via {@link useWorkingTimeNoticeDialogContext} — so the UI pieces consume
 * {@code {state, actions, meta}} from context instead of having the controller prop-drilled through
 * every component.
 */
export const WorkingTimeNoticeDialogProvider = WorkingTimeNoticeDialogContext.Provider;

/**
 * Consumer side of the dialog controller — reads the single controller instance out of context.
 *
 * This is the counterpart to {@link useWorkingTimeNoticeDialogController}: the controller hook
 * *produces* the {@code {state, actions, meta}} instance once at the variant root and feeds it into
 * {@link WorkingTimeNoticeDialogProvider}; this hook lets any descendant *consume* that same instance
 * without prop-drilling. Because it returns the already-built controller, it takes no options and may
 * be called by as many components as needed (shell, footer buttons, …) — they all share one state.
 *
 * Throws when used outside a provider, which can only happen by wiring mistake — so the consuming UI
 * components never have to null-check.
 *
 * @see useWorkingTimeNoticeDialogController for the producer side.
 */
export function useWorkingTimeNoticeDialogContext(): WorkingTimeNoticeDialogController {
	const controller = use(WorkingTimeNoticeDialogContext);
	if (controller === null) {
		throw new Error("useWorkingTimeNoticeDialogContext must be used within a WorkingTimeNoticeDialogProvider");
	}
	return controller;
}

interface WorkingTimeNoticeDialogControllerCommonOptions {
	open: boolean;
	initialForm: WorkingTimeNoticeForm;
	workingTimeTypes: WorkingTimeType[];
	showUserRow: boolean;
	/** Whether the user may be picked — true for create, false for edit (the user of an existing notice is fixed). */
	userSelectable: boolean;
	/** Whether the current user is a leader of a team they belong to — used to decide edit permission for own notices. */
	isGroupLeaderForThemself: boolean;
	/**
	 * Whether the dialog is view-only. Derived state (the notice's type is not one this user may author),
	 * supplied by the variant — see {@code isNoticeReadOnly} in {@code workingTimeNoticeDialog.tsx} — so a
	 * single source decides it once and uses it for both the controller (field disabling) and the
	 * variant's own footer/title composition. A single derived flag, not a behavioural mode: it makes the
	 * existing form inert (like {@code disabled}), it does not add an alternate flow.
	 */
	readOnly: boolean;
	/**  Whether the dialog stays open after saving to create a further notice.*/
	createAnother?: boolean;
	onSaved: () => void;
	onDeleted: () => void;
	onClose: () => void;
}

export type WorkingTimeNoticeDialogControllerOptions = WorkingTimeNoticeDialogControllerCommonOptions;

/**
 * Derives the initial day span between the form's start and end so that, after a start-date edit,
 * the end date can shift while preserving the original duration.
 */
function initialDayDifference(form: WorkingTimeNoticeForm): number {
	return form.start && form.end ? form.start.until(form.end).days : 0;
}

/**
 * Producer side of the dialog controller — builds the single {@code {state, actions, meta}} instance
 * that drives one dialog: it owns the form state and the save/delete API calls.
 *
 * State is initialized directly from {@code initialForm} (which carries the seeded selected user). There is no
 * re-initialization logic: the dialog is remounted (via a {@code key} on the owning component) when
 * its identity changes, so a fresh mount — and thus fresh {@code useState} — is what resets the form.
 *
 * Call this exactly once, at the owning component, then pass the result into
 * {@link WorkingTimeNoticeDialogProvider}. Descendants read the same instance via
 * {@link useWorkingTimeNoticeDialogContext} — never by calling this hook again, as each call creates
 * its own independent state (separate form, separate {@code saving} flag, …).
 *
 * @see useWorkingTimeNoticeDialogContext for the consumer side.
 */
export function useWorkingTimeNoticeDialogController({
														 open,
														 initialForm,
														 workingTimeTypes,
														 showUserRow,
														 userSelectable,
														 readOnly,
														 createAnother,
														 onSaved,
														 onDeleted,
														 onClose
													 }: WorkingTimeNoticeDialogControllerOptions): WorkingTimeNoticeDialogController {
	const [form, setForm] = useState<WorkingTimeNoticeForm>(initialForm);
	const [saving, setSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	// Tracks the day span between start and end so the end date shifts when the start date changes
	// while keeping the same duration. Seeded from the initial form (the argument is evaluated each
	// render but only the first value is kept).
	const daysDateDifferenceRef = useRef(initialDayDifference(initialForm));

	const setStartDate = (newStart: Temporal.PlainDate | null) => {
		setForm(prev => ({...prev, start: newStart}));
		if (daysDateDifferenceRef.current < 0) {
			return;
		}
		if (newStart) {
			// Shift the end date to keep the original duration
			const nextEnd = newStart.add({days: daysDateDifferenceRef.current});
			setForm(prev => ({...prev, end: nextEnd}));
		}
	};

	const setEndDate = (newEnd: Temporal.PlainDate | null) => {
		if (form.start && newEnd) {
			daysDateDifferenceRef.current = form.start.until(newEnd).days;
		}
		setForm(prev => ({...prev, end: newEnd}));
	};

	/**
	 * "Create another": seed the form for the next notice — the period moves to the day after the saved one, while user,
	 * working time type and description stay as they are. Only the create variant offers the checkbox.
	 */
	const continueWithNextNotice = () => {
		setForm(prev => {
			const nextStart = (prev.end ?? prev.start)?.add({days: 1}) ?? null;
			const days = Math.max(daysDateDifferenceRef.current, 0);
			return {...prev, start: nextStart, end: nextStart?.add({days}) ?? null};
		});
	};

	/**
	 * Posts the form as a notice or, for a working time type that requires a request, as a notice request — the
	 * same switch the absence dialog makes for working times. Both endpoints share the payload and its field errors.
	 */
	const submit = (approveImmediately: boolean) => {
		// userId is left absent when no user is selected (regular employee with no user row, or a leader
		// who has not picked one); the backend then defaults it to the current user — the established
		// convention across the legacy dialogs (WorkingTimeRequest, DriveLog) and WorkingTimeNoticeController.
		const payload: SaveWorkingTimeNoticeRequest = {
			id: form.id,
			version: form.version,
			userId: form.selectedUser?.id,
			workingTimeTypeId: form.selectedWorkingTimeType?.id,
			description: form.description,
			start: getISODate(form.start),
			end: getISODate(form.end)
		};

		setFieldErrors({});
		setSaving(true);
		const saveRequest = isNoticeRequestForm(form)
			? api("/notices/saveWorkingTimeNoticeRequest.json").post(approveImmediately
				? {...payload, status: WorkingTimeRequestStatus.Approved}
				: payload)
			: api("/notices/saveWorkingTimeNotice.json").post(payload);
		void saveRequest
			.done(() => {
				if (createAnother) {
					continueWithNextNotice();
				} else {
					onClose();
				}
				onSaved();
			})
			.fail((jqXHR: { responseJSON?: { fieldErrors?: FieldError[] } }) => {
				const errors = jqXHR.responseJSON?.fieldErrors ?? [];
				const record: Record<string, string> = {};
				for (const e of errors) {
					if (!(e.field in record)) record[e.field] = e.message;
				}
				setFieldErrors(record);
			})
			.always(() => setSaving(false));
	};

	const save = () => submit(false);

	const saveAndApprove = () => submit(true);

	const deleteNotice = () => {
		if (form.id == null) {
			return;
		}

		BootstrapModal.confirm({
			title: "workingTimeNotice.confirmDelete.title",
			message: messages["workingTimeNotice.confirmDelete"],
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				void api("/notices/deleteWorkingTimeNotice.json")
					.post({id: form.id})
					.done(() => {
						onClose();
						onDeleted();
					})
					.always(() => modal.close());
			}
		});
	};

	const setWorkingTimeTypeId = (id: number) => {
		// The id always comes from the rendered <select>, whose options are exactly `workingTimeTypes`,
		// so the lookup resolves; the fallback only guards against an unexpected stray id.
		const option = workingTimeTypes.find(wtt => wtt.id === id);
		setForm(prev => ({
			...prev,
			selectedWorkingTimeType: option
				? {id: option.id, name: option.name, category: option.category, noticeMode: option.noticeMode}
				: prev.selectedWorkingTimeType
		}));
	};

	const setDescription = (value: string) => {
		setForm(prev => ({...prev, description: value}));
	};

	const setSelectedUser = (user: UserOption | null) => {
		setForm(prev => ({...prev, selectedUser: user}));
	};

	return {
		state: {
			open,
			form,
			saving,
			fieldErrors
		},
		actions: {
			setWorkingTimeTypeId,
			setDescription,
			setStartDate,
			setEndDate,
			setSelectedUser,
			save,
			saveAndApprove,
			deleteNotice,
			close: onClose
		},
		meta: {
			showUserRow,
			userSelectable,
			workingTimeTypes,
			readOnly
		}
	};
}
